import { migrateV1Cosmetics, type V1Cosmetics } from "./kartLoadout";
import type {
  DriverAnimationProfile,
  DriverCelebrationStyle,
  DriverItemTactics,
  DriverProfile,
  DriverProfileAppearance,
  DriverProfileBarks,
  DriverProfileTraits,
} from "./driverProfiles";

/**
 * The v1 server exposed these six personality controls.  They are kept as a
 * source-shaped record here so the v2 adaptation remains auditable against
 * `shared/sim.js`, rather than silently inventing a second set of values.
 */
interface LegacyDriverPersonality {
  archetype: string;
  aggression: number;
  itemSeek: number;
  itemUse: number;
  lineBias: number;
  recovery: number;
  skill: number;
}

interface LegacyDriverSource {
  kartId: string;
  id: string;
  name: string;
  title: string;
  signature: string;
  cosmetics: V1Cosmetics;
  personality: LegacyDriverPersonality;
  celebration: DriverCelebrationStyle;
}

const LEGACY_DRIVER_SOURCES: readonly LegacyDriverSource[] = [
  {
    kartId: "kart-8", id: "legacy-stout-steve", name: "StoutSteve", title: "The Old-School Brawler",
    signature: "A hard-charging line from the original grid",
    cosmetics: { b: "bottle", c: "#4b2c20", a: "#f3c95c", p: "stripe", h: "crown", s: "#120c08", t: "flame" },
    personality: { archetype: "Brawler", aggression: 0.90, itemSeek: 0.30, itemUse: 0.90, lineBias: 0.08, recovery: 0.60, skill: 0.65 },
    celebration: "warrior-punch",
  },
  {
    kartId: "kart-9", id: "legacy-lager-lars", name: "LagerLars", title: "The Smooth Classic",
    signature: "A measured line carried over from v1",
    cosmetics: { b: "can", c: "#e6c64b", a: "#fff3c4", p: "stripe", h: "cap", s: "#5a3a12", t: "bubbles" },
    personality: { archetype: "Smooth", aggression: 0.25, itemSeek: 0.40, itemUse: 0.45, lineBias: -0.06, recovery: 0.75, skill: 0.90 },
    celebration: "stoic-nod",
  },
  {
    kartId: "kart-10", id: "legacy-cider-cindy", name: "CiderCindy", title: "The Barrel Collector",
    signature: "A box-hunting line from the original grid",
    cosmetics: { b: "mug", c: "#d9823e", a: "#6fbf55", p: "split", h: "lime", s: "#3a5a12", t: "hops" },
    personality: { archetype: "Collector", aggression: 0.35, itemSeek: 0.95, itemUse: 0.55, lineBias: 0.18, recovery: 0.55, skill: 0.60 },
    celebration: "scout-salute",
  },
  {
    kartId: "kart-11", id: "legacy-ale-alan", name: "AleAlan", title: "The Timely Opportunist",
    signature: "A perfectly timed move from v1",
    cosmetics: { b: "bottle", c: "#b8632f", a: "#17110d", p: "stripe", h: "cap", s: "#120c08", t: "flame" },
    personality: { archetype: "Opportunist", aggression: 0.70, itemSeek: 0.65, itemUse: 0.75, lineBias: -0.12, recovery: 0.65, skill: 0.80 },
    celebration: "fist-pump",
  },
  {
    kartId: "kart-12", id: "legacy-pils-petra", name: "PilsPetra", title: "The Precision Ace",
    signature: "Clean execution carried over from v1",
    cosmetics: { b: "can", c: "#57c7dc", a: "#f4c542", p: "split", h: "crown", s: "#1f4d7a", t: "neon" },
    personality: { archetype: "Ace", aggression: 0.45, itemSeek: 0.35, itemUse: 0.60, lineBias: 0, recovery: 0.80, skill: 1.00 },
    celebration: "precision-clap",
  },
  {
    kartId: "kart-13", id: "legacy-porto-paul", name: "PortoPaul", title: "The Patient Survivor",
    signature: "A steady finish from the original grid",
    cosmetics: { b: "keg", c: "#6e263d", a: "#f3ddbd", p: "stripe", h: "none", s: "#4a1f5a", t: "foam" },
    personality: { archetype: "Survivor", aggression: 0.20, itemSeek: 0.45, itemUse: 0.35, lineBias: -0.08, recovery: 1.00, skill: 0.60 },
    celebration: "stoic-nod",
  },
  {
    kartId: "kart-14", id: "legacy-wheat-will", name: "WheatWill", title: "The Wildcard Return",
    signature: "Unpredictable pressure from the old grid",
    cosmetics: { b: "mug", c: "#d8aa55", a: "#2d78aa", p: "stripe", h: "lime", s: "#5a3a12", t: "hops" },
    personality: { archetype: "Wildcard", aggression: 0.80, itemSeek: 0.80, itemUse: 1.00, lineBias: -0.18, recovery: 0.40, skill: 0.50 },
    celebration: "wild-wave",
  },
  {
    kartId: "kart-15", id: "legacy-hop-harry", name: "HopHarry", title: "The Hoppy Brawler",
    signature: "Relentless attacks from the original grid",
    cosmetics: { b: "keg", c: "#4f813d", a: "#e0c85a", p: "split", h: "cap", s: "#3a5a12", t: "hops" },
    personality: { archetype: "Brawler", aggression: 0.85, itemSeek: 0.35, itemUse: 0.85, lineBias: 0.10, recovery: 0.70, skill: 0.70 },
    celebration: "fist-pump",
  },
  {
    kartId: "kart-16", id: "legacy-malt-mona", name: "MaltMona", title: "The Patient Collector",
    signature: "A well-stocked route from v1",
    cosmetics: { b: "bottle", c: "#a87450", a: "#f4ddbd", p: "solid", h: "none", s: "#5a3a12", t: "bubbles" },
    personality: { archetype: "Collector", aggression: 0.30, itemSeek: 0.90, itemUse: 0.50, lineBias: 0.15, recovery: 0.65, skill: 0.65 },
    celebration: "scout-salute",
  },
  {
    kartId: "kart-17", id: "legacy-gin-gina", name: "GinGina", title: "The Stormy Wildcard",
    signature: "A volatile route from the old grid",
    cosmetics: { b: "can", c: "#7255a6", a: "#4ce4e0", p: "split", h: "crown", s: "#4a1f5a", t: "neon" },
    personality: { archetype: "Wildcard", aggression: 0.75, itemSeek: 0.75, itemUse: 0.95, lineBias: -0.16, recovery: 0.45, skill: 0.55 },
    celebration: "storm-thrash",
  },
  {
    kartId: "kart-18", id: "legacy-rum-rita", name: "RumRita", title: "The Calm Survivor",
    signature: "A resilient finish from the original grid",
    cosmetics: { b: "mug", c: "#a83e3e", a: "#f5e2b8", p: "stripe", h: "lime", s: "#7a1f1f", t: "foam" },
    personality: { archetype: "Survivor", aggression: 0.25, itemSeek: 0.50, itemUse: 0.40, lineBias: -0.05, recovery: 0.95, skill: 0.70 },
    celebration: "stoic-nod",
  },
  {
    kartId: "kart-19", id: "legacy-vodka-vern", name: "VodkaVern", title: "The Opportunist Classic",
    signature: "A decisive opening from v1",
    cosmetics: { b: "keg", c: "#9fa8b2", a: "#4b8fd8", p: "split", h: "cap", s: "#1f4d7a", t: "neon" },
    personality: { archetype: "Opportunist", aggression: 0.65, itemSeek: 0.60, itemUse: 0.70, lineBias: 0.05, recovery: 0.70, skill: 0.85 },
    celebration: "precision-clap",
  },
] as const;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

/**
 * Map the old personality dimensions into v2's bounded decision traits.
 * `aggression` remains pass aggression; `itemSeek` becomes shortcut tendency;
 * `itemUse` is inverted into item patience; signed `lineBias` becomes the
 * small v2 pace bias (with lane width/tempo derived from its magnitude);
 * `recovery` becomes defensive and hazard awareness; and `skill` becomes
 * drift commitment and the remaining hazard/boost confidence.  These are
 * deliberately bounded behavioural approximations: v2's steering, collision,
 * recovery and item systems are different, so the mapping is not simulation
 * equivalence and never grants hidden physics authority.
 */
function mapLegacyTraits(personality: LegacyDriverPersonality): DriverProfileTraits {
  const aggression = clamp(personality.aggression, 0, 1);
  const itemSeek = clamp(personality.itemSeek, 0, 1);
  const itemUse = clamp(personality.itemUse, 0, 1);
  const lineBias = clamp(personality.lineBias, -0.18, 0.18);
  const recovery = clamp(personality.recovery, 0, 1);
  const skill = clamp(personality.skill, 0, 1);
  const lineMagnitude = Math.abs(lineBias);

  return {
    passAggression: aggression,
    laneWidth: clamp(1.4 + aggression * 2.4 + lineMagnitude * 5.5, 1.4, 4.8),
    laneTempo: clamp(0.1 + aggression * 0.16 + itemUse * 0.05 + lineMagnitude * 0.18, 0.1, 0.34),
    shortcutTendency: itemSeek,
    driftCommitment: skill,
    itemPatience: 1 - itemUse,
    paceBias: clamp(lineBias * (0.04 / 0.18), -0.04, 0.04),
    defenseTendency: recovery,
    hazardAwareness: clamp(skill * 0.65 + recovery * 0.35, 0, 1),
  };
}

/** Map v1's item-use/recovery/skill controls to v2's bounded item windows. */
function mapLegacyItemTactics(personality: LegacyDriverPersonality): DriverItemTactics {
  const itemSeek = clamp(personality.itemSeek, 0, 1);
  const itemUse = clamp(personality.itemUse, 0, 1);
  const recovery = clamp(personality.recovery, 0, 1);
  const skill = clamp(personality.skill, 0, 1);
  return {
    kegRange: 40 + itemUse * 80,
    foamThreatRange: 20 + recovery * 30,
    shieldPlace: clamp(Math.round(8 - recovery * 6), 2, 8),
    comebackPlace: clamp(Math.round(8 - itemSeek * 3), 5, 8),
    boostCurvature: clamp(0.22 + skill * 0.55, 0.2, 0.9),
  };
}

/** The compact v1 object is expanded through the canonical v2 migration path. */
function migrateLegacyAppearance(name: string, cosmetics: V1Cosmetics): DriverProfileAppearance {
  const loadout = migrateV1Cosmetics(cosmetics, { name });
  return {
    color: loadout.color,
    accentColor: loadout.accentColor,
    variant: loadout.variant,
    livery: loadout.livery,
    wheelStyle: loadout.wheelStyle,
    driverColor: loadout.driverColor,
    weightClass: loadout.weightClass,
    pattern: loadout.pattern,
    topper: loadout.topper,
    sticker: loadout.sticker,
    underglow: loadout.underglow,
    horn: loadout.horn,
    skidColor: loadout.skidColor,
    trail: loadout.trail,
    raceNumber: loadout.raceNumber,
  };
}

function createLegacyBarks(name: string, archetype: string): DriverProfileBarks {
  return {
    intro: [`${name} brings the ${archetype.toLowerCase()} line back.`],
    pass: [`${name} makes the old-school move.`],
    hit: [`${name} shakes it off.`],
    win: [`${name} still knows the winning pour.`],
  };
}

function createLegacyAnimation(source: LegacyDriverSource, personality: LegacyDriverPersonality): DriverAnimationProfile {
  const aggression = clamp(personality.aggression, 0, 1);
  const itemUse = clamp(personality.itemUse, 0, 1);
  const itemSeek = clamp(personality.itemSeek, 0, 1);
  const lineMagnitude = Math.abs(clamp(personality.lineBias, -0.18, 0.18));
  const skill = clamp(personality.skill, 0, 1);
  return {
    celebration: source.celebration,
    lean: clamp(0.72 + aggression * 0.55, 0.5, 1.5),
    bob: clamp(0.62 + itemUse * 0.6, 0.5, 1.5),
    tempo: clamp(0.7 + aggression * 0.65 + skill * 0.25, 0.5, 1.8),
    headScan: clamp(0.15 + itemSeek * 0.6 + lineMagnitude * 0.5, 0, 1),
  };
}

function createLegacyProfile(source: LegacyDriverSource): DriverProfile {
  const appearance = migrateLegacyAppearance(source.name, source.cosmetics);
  const traits = mapLegacyTraits(source.personality);
  return {
    id: source.id,
    kartId: source.kartId,
    name: source.name,
    title: source.title,
    signature: source.signature,
    color: appearance.color,
    appearance: Object.freeze(appearance),
    traits: Object.freeze(traits),
    itemTactics: Object.freeze(mapLegacyItemTactics(source.personality)),
    barks: Object.freeze(createLegacyBarks(source.name, source.personality.archetype)),
    animation: Object.freeze(createLegacyAnimation(source, source.personality)),
  };
}

/** The twelve named v1 drivers, assigned to the additional full-race slots. */
export const LEGACY_DRIVER_PROFILES: readonly DriverProfile[] = Object.freeze(LEGACY_DRIVER_SOURCES.map(createLegacyProfile));

/**
 * Select a stable legacy personality for one bot in a race.
 *
 * The v1 server advanced the roster by the configured minimum-bot count on
 * every race. Keep the arithmetic bounded before multiplying so a long-lived
 * server cannot lose determinism through unsafe integer overflow.
 */
export function selectLegacyDriverProfile(raceNo: number, minBots: number, botIndex: number): DriverProfile {
  if (!Number.isSafeInteger(raceNo) || raceNo < 0) throw new RangeError("raceNo must be a non-negative safe integer");
  if (!Number.isSafeInteger(minBots) || minBots < 0 || minBots > LEGACY_DRIVER_PROFILES.length) throw new RangeError(`minBots must be an integer from 0 to ${LEGACY_DRIVER_PROFILES.length}`);
  if (!Number.isSafeInteger(botIndex) || botIndex < 0 || botIndex >= LEGACY_DRIVER_PROFILES.length) throw new RangeError(`botIndex must be an integer from 0 to ${LEGACY_DRIVER_PROFILES.length - 1}`);
  const profileIndex = ((raceNo % LEGACY_DRIVER_PROFILES.length) * (minBots % LEGACY_DRIVER_PROFILES.length) + botIndex) % LEGACY_DRIVER_PROFILES.length;
  return LEGACY_DRIVER_PROFILES[profileIndex];
}
